import chunk from 'lodash/chunk';
import {getEnv} from '../../../../../../common/env/env';
import {getKageUrl} from '../../../../../../common/shared/utils/getKageUrl';
import {PagewebError} from '../../../../../../errors/PagewebError.class';
import {PagewebErrorType} from '../../../../../../errors/PagewebErrorType.enum';
import {ContentInfo} from '../type/ContentInfo.interface';
import {ContentMetaInfo} from '../type/ContentMetaInfo.interface';
import {ContentMetaResponseData} from '../type/ContentMetaResponseData.interface';
import {ContentResponseData} from '../type/ContentResponseData.interface';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {getParagraphNode} from '../util/getParagraphNode';
import {ContentLoadAsset} from './ContentLoadAsset.class';
import {LoadAsset} from './LoadAsset.class';
import {LoadAssetGroup} from './LoadAssetGroup.class';
import {Loadable} from './Loadable.type';
import {Loader} from './Loader.class';
import {MetaLoadAsset} from './MetaLoadAsset.class';
import {PreloadChapterAssetGroup} from './PreloadChapterAssetGroup.class';
import {RestChunkAssetGroup} from './RestChunkAssetGroup.class';
import {StyleAssetGroup} from './StyleAssetGroup.class';
import {StyleLoadAsset} from './StyleLoadAsset.class';

interface ContentUrlInfo {
  chapterId: number;
  contentId: number;
  url: string;
}

interface LoadedInfo {
  chapterCount: number;
  chapterInfoList: ChapterInfo[];
}

interface ChapterInfo {
  chapterId: number;
  contentCount: number;
  paragraphCount: number;
  contentHtmlList: string[];
  styleCount: number;
  styleList: string[];
}

export class TextViewerLoader {
  private _seriesId: string;
  private _productId: string;
  private _metaUrl: string;
  private _contentUrlInfoList: ContentUrlInfo[];

  private _loadableList: Loadable[] = [];
  private _loader = new Loader();

  public onMetaLoaded?: (loader: TextViewerLoader) => void;
  public onContentLoaded?: (loader: TextViewerLoader) => void;
  public onStyleLoaded?: (loader: TextViewerLoader) => void;
  public onContentPreloaded?: (
    loader: TextViewerLoader,
    chapterId: number
  ) => void;
  public onContentChunkLoaded?: (loader: TextViewerLoader) => void;
  public onChapterStyleLoaded?: (loader: TextViewerLoader) => void;
  public onChapterLoaded?: (
    loader: TextViewerLoader,
    chapterId: number
  ) => void;

  public metaInfo?: ContentMetaInfo;
  public contentInfoList?: ContentInfo[];

  public latestLoadable?: Loadable;
  public loadedInfo?: LoadedInfo;

  constructor({
    seriesId,
    productId,
    metaUrl,
    contentUrlInfoList,
  }: {
    seriesId: string;
    productId: string;
    metaUrl: string;
    contentUrlInfoList: ContentUrlInfo[];
  }) {
    this._seriesId = seriesId;
    this._productId = productId;
    this._metaUrl = metaUrl;
    this._contentUrlInfoList = contentUrlInfoList;
  }

  ready = (option?: {preloadChapterIds?: number[]; chunkSize?: number}) => {
    this._loadableList = [];

    const metaAsset = new MetaLoadAsset({
      seriesId: this._seriesId,
      productId: this._productId,
      url: this._metaUrl,
    });
    this._loadableList.push(metaAsset);

    const preloadInfoList: {chapterId: number; urlList: ContentUrlInfo[]}[] =
      [];
    let restContentUrlInfoList = this._contentUrlInfoList;

    const preloadChapterIds = option?.preloadChapterIds;
    if (preloadChapterIds) {
      for (const chapterId of preloadChapterIds) {
        const filteredList = restContentUrlInfoList.filter(
          contentUrlInfo => contentUrlInfo.chapterId === chapterId
        );
        preloadInfoList.push({chapterId, urlList: filteredList});

        restContentUrlInfoList = restContentUrlInfoList.filter(
          contentUrlInfo => contentUrlInfo.chapterId !== chapterId
        );
      }
    }

    for (const preloadInfo of preloadInfoList) {
      const preloadAssetList = preloadInfo.urlList.map(
        contentUrlInfo => new ContentLoadAsset(contentUrlInfo)
      );
      const preloadAssetGroup = new PreloadChapterAssetGroup({
        chapterId: preloadInfo.chapterId,
        loadAssetList: preloadAssetList,
      });
      this._loadableList.push(preloadAssetGroup);
    }

    const chunkSize = option?.chunkSize || DEFAULT_CHUNK_SIZE;
    const restList = chunk(restContentUrlInfoList, chunkSize);
    for (const restContentUrlInfoList of restList) {
      const restAssetList = restContentUrlInfoList.map(
        contentUrlInfo => new ContentLoadAsset(contentUrlInfo)
      );
      const restChunkAssetGroup = new RestChunkAssetGroup(restAssetList);
      this._loadableList.push(restChunkAssetGroup);
    }
  };

  load = async () => {
    this._loader.clear();
    this._loader.appendLoadable(...this._loadableList);
    const loadGenerator = this._loader.loadGenerator();
    for await (const loadable of loadGenerator) {
      if (loadable.error) {
        throw new PagewebError(loadable.error);
      }
      this.latestLoadable = loadable;
      if (loadable instanceof LoadAssetGroup) {
        this.processLoadAssetGroup(loadable);
      } else if (loadable instanceof LoadAsset) {
        this.processLoadAsset(loadable);
      } else {
        throw new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.UnknownError,
          message: 'loadable 인스턴스가 처리할 수 있는 Load 타입이 아닙니다.',
        });
      }
    }
  };

  private processLoadAssetGroup = (loadAssetGroup: LoadAssetGroup) => {
    if (loadAssetGroup.error) {
      throw new PagewebError(loadAssetGroup.error);
    }
    if (!loadAssetGroup.resultList) {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: 'loadAssetGroup 인스턴스의 결과가 존재하지 않습니다.',
      });
    }
    loadAssetGroup.loadAssetList.forEach(this.processLoadAsset);

    if (loadAssetGroup instanceof PreloadChapterAssetGroup) {
      this.onContentPreloaded?.(this, loadAssetGroup.chapterId);
    } else if (loadAssetGroup instanceof RestChunkAssetGroup) {
      this.onContentChunkLoaded?.(this);
    } else if (loadAssetGroup instanceof StyleAssetGroup) {
      this.onChapterStyleLoaded?.(this);
    } else {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message:
          'loadAssetGroup 인스턴스가 처리할 수 있는 AssetGroup 타입이 아닙니다.',
      });
    }
  };

  private processLoadAsset = (loadAsset: LoadAsset) => {
    if (loadAsset.error) {
      throw new PagewebError(loadAsset.error);
    }
    if (!loadAsset.result) {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: 'loadAsset 인스턴스의 결과가 존재하지 않습니다.',
      });
    }
    if (loadAsset instanceof MetaLoadAsset) {
      this.processMetaData(loadAsset.result);
      this.onMetaLoaded?.(this);
    } else if (loadAsset instanceof ContentLoadAsset) {
      this.processContentData(
        loadAsset.result,
        loadAsset.groupType === 'PreloadChapter'
          ? loadAsset.chapterId
          : undefined
      );
      this.onContentLoaded?.(this);
    } else if (loadAsset instanceof StyleLoadAsset) {
      this.processStyleData({
        chapterId: loadAsset.chapterId,
        data: loadAsset.result,
      });
      this.onStyleLoaded?.(this);
    } else {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: 'loadAsset 인스턴스가 처리할 수 있는 Asset 타입이 아닙니다.',
      });
    }
  };

  private processMetaData = (data: ContentMetaResponseData) => {
    this.metaInfo = data.contentMetaInfo;
    this.contentInfoList = data.contentDownloadInfo.contentList;

    this.loadedInfo = {
      chapterCount: this.metaInfo.spineInfo.spines.length,
      chapterInfoList: [],
    };
  };

  private processContentData = (
    data: ContentResponseData,
    preloadChapterId?: number
  ) => {
    const {chapterId, contentId, styleList, paragraphList} = data.contentInfo;
    if (contentId === 0) {
      const styleAssetList = styleList.map(
        styleUrlInfo =>
          new StyleLoadAsset({
            chapterId,
            url: getKageUrl({
              kageUrlPrefix: getEnv().kageUrlPrefix,
              kageId: styleUrlInfo.src,
              version: '1',
            }),
          })
      );
      let loadAssetGroup;
      if (preloadChapterId !== undefined) {
        loadAssetGroup = new PreloadChapterAssetGroup({
          chapterId: preloadChapterId,
          loadAssetList: styleAssetList,
        });
      } else {
        loadAssetGroup = new StyleAssetGroup(styleAssetList);
      }
      this._loader.insertLoadable(loadAssetGroup);
    }

    const tempDivElement = document.createElement('div');
    paragraphList.forEach(paragraph => {
      const node = getParagraphNode(paragraph);
      tempDivElement.appendChild(node);
    });
    const html = tempDivElement.innerHTML;

    if (!this.loadedInfo) {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: 'loadedInfo가 존재하지 않습니다.',
      });
    }
    let chapterInfo = this.loadedInfo.chapterInfoList[chapterId];
    if (!chapterInfo) {
      const contentSpine = this.metaInfo?.spineInfo.spines.find(
        spine => Number(spine.chapterId) === chapterId
      );
      if (!contentSpine) {
        throw new PagewebError({
          isSerializedPagewebError: true,
          type: PagewebErrorType.UnknownError,
          message: '해당 spine이 없습니다.',
        });
      }
      chapterInfo = {
        chapterId,
        contentCount: contentSpine.countContent,
        paragraphCount: contentSpine.countParagraph,
        contentHtmlList: [],
        styleCount: styleList.length,
        styleList: [],
      };
    }
    chapterInfo.contentHtmlList[contentId] = html;
    this.loadedInfo.chapterInfoList[chapterId] = chapterInfo;

    if (this.isChapterLoaded(chapterId)) {
      this.onChapterLoaded?.(this, chapterId);
    }
  };

  private processStyleData = ({
    chapterId,
    data,
  }: {
    chapterId: number;
    data: string;
  }) => {
    if (!this.loadedInfo) {
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: 'loadedInfo가 존재하지 않습니다.',
      });
    }
    const chapterInfo = this.loadedInfo.chapterInfoList[chapterId];
    if (!chapterInfo) {
      // content 정보를 가져와야 style 정보를 알수있습니다.
      // 즉, 이 시점에는 최소 1개 이상의 content를 로드한 상황이므로 이를 담은 chapter 정보가 있어야합니다.
      throw new PagewebError({
        isSerializedPagewebError: true,
        type: PagewebErrorType.UnknownError,
        message: '챕터 정보가 존재하지 않습니다.',
      });
    }
    chapterInfo.styleList.push(data);
    this.loadedInfo.chapterInfoList[chapterId] = chapterInfo;

    if (this.isChapterLoaded(chapterId)) {
      this.onChapterLoaded?.(this, chapterId);
    }
  };

  private isChapterLoaded = (chapterId: number) => {
    if (!this.loadedInfo) {
      return false;
    }
    const chapterInfo = this.loadedInfo.chapterInfoList[chapterId];
    if (!chapterInfo) {
      return false;
    }
    const {styleCount, styleList, contentCount, contentHtmlList} = chapterInfo;
    if (
      styleCount !== styleList.length ||
      contentCount !== contentHtmlList.length ||
      chapterInfo.contentHtmlList.some(html => html === undefined)
    ) {
      return false;
    }
    return true;
  };

  getChapter: (chapterId: number) => HtmlInfo | undefined = chapterId => {
    if (!this.isChapterLoaded(chapterId)) {
      return;
    }
    const chapterInfo = this.loadedInfo?.chapterInfoList[chapterId];
    if (
      !chapterInfo ||
      !chapterInfo.contentHtmlList ||
      !chapterInfo.styleList
    ) {
      return;
    }
    return {
      html: chapterInfo.contentHtmlList.join('\n'),
      style: chapterInfo.styleList.join('\n'),
    };
  };

  getAllChapters: () => HtmlInfo[] | undefined = () => {
    const loadedInfo = this.loadedInfo;
    if (!loadedInfo) {
      return;
    }
    const {chapterCount, chapterInfoList} = loadedInfo;
    if (chapterCount !== chapterInfoList.length) {
      return;
    }
    const result: HtmlInfo[] = [];
    for (let i = 0; i < chapterInfoList.length; i++) {
      const chapter = this.getChapter(i);
      if (!chapter) {
        return;
      }
      result.push(chapter);
    }
    return result;
  };
}

const DEFAULT_CHUNK_SIZE = 10;
